import { useLocation, useNavigate } from "react-router-dom";

import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useDealCaptureLookups } from "@/hooks/useDealCapture";
import { flowInScope } from "@/lib/deal-capture/flow";
import { FLOW_PARAM, areaPath, locate } from "@/lib/deal-capture/paths";
import type { SidebarSlotProps } from "@/lib/module-registry";
import type { DealFlow } from "@/types/deal-capture";

/**
 * Integrated / Non-Integrated: which deal flow the Deals area shows. Rendered
 * only while the area is Deals (`DealCaptureSidebarHeader`).
 *
 * Kept in the url (`?flow=non_integrated`), so a link or a reload keeps the
 * flow, and Integrated, the default, leaves the url clean. With a deal open
 * the toggle shows that deal's own flow, and switching it leaves the deal for
 * the other flow's Summary, since the deal does not belong to it.
 */
export function FlowToggle({ onClose }: SidebarSlotProps) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { data } = useDealCaptureLookups();
  const { recordId } = locate(pathname);
  const openDeal = data?.deals.find((d) => d.number === recordId);
  const flow = flowInScope(search, openDeal);

  const choose = (next: string) => {
    // Radix reports "" when the pressed item is clicked again; a flow is
    // always chosen, so that click changes nothing.
    if (next !== "integrated" && next !== "non_integrated") return;
    const params = new URLSearchParams(openDeal ? "" : search);
    if ((next as DealFlow) === "integrated") params.delete(FLOW_PARAM);
    else params.set(FLOW_PARAM, next);
    const query = params.toString();
    navigate(`${openDeal ? areaPath("deals") : pathname}${query ? `?${query}` : ""}`, {
      replace: !openDeal,
    });
    // On a list or Summary only the query string changes, so the shell will
    // not close the mobile drawer on its own.
    onClose();
  };

  return (
    <ToggleGroup
      type="single"
      value={flow}
      onValueChange={choose}
      aria-label="Deal flow"
      className="grid w-full grid-cols-2"
    >
      <ToggleGroupItem value="integrated" className="whitespace-nowrap px-2">
        Integrated
      </ToggleGroupItem>
      <ToggleGroupItem value="non_integrated" className="whitespace-nowrap px-2">
        Non-Integrated
      </ToggleGroupItem>
    </ToggleGroup>
  );
}
